import jsCookie from 'js-cookie';

import rules, { type RuleType } from './block-rules';
import { cookieCategories } from './cookie-categories';

const CONSENT_CHANGE_EVENT_NAME = 'consent.onetrust';
export const CONSENT_BANNER_COOKIE = 'OptanonAlertBoxClosed';
export const CONSENT_CHANGED_EVENT_NAME = 'OneTrustConsentChanged';

type Listener = (event: CustomEvent) => void;

declare global {
  interface Window {
    consentChangedListeners: Set<Listener>;
    OnetrustActiveGroups?: string;
    OneTrust?: {
      ToggleInfoDisplay: () => void;
      LoadBanner: () => void;
      changeLanguage: (locale: string) => void;
    };
  }
}

if (typeof window !== 'undefined') {
  window.consentChangedListeners = window.consentChangedListeners || new Set();
}

const onetrust = {
  observer: null as MutationObserver | null,

  initialize(): void {
    this.setBlocker();
    this.initConsentChangedListener();
  },

  getOneTrustActiveGroups(): string[] {
    if (!window.OnetrustActiveGroups) {
      return ['C0001'];
    }

    const groups = window.OnetrustActiveGroups;
    return groups.split(',').filter(Boolean);
  },

  initConsentChanged(): void {
    const activeGroups = this.getOneTrustActiveGroups();
    const event = new CustomEvent(CONSENT_CHANGED_EVENT_NAME, {
      detail: activeGroups,
    });

    const listeners = Array.from(window.consentChangedListeners);
    for (const listener of listeners) {
      listener(event);
    }
    this.filterScripts();
  },

  initConsentChangedListener(): void {
    window.addEventListener(CONSENT_CHANGE_EVENT_NAME, this.initConsentChanged.bind(this));
  },

  deinitialize(): void {
    window.consentChangedListeners.clear();

    window.removeEventListener(CONSENT_CHANGE_EVENT_NAME, this.initConsentChanged);

    this.clearObserver();
  },

  openPreferenceCenter(): void {
    window.OneTrust?.ToggleInfoDisplay();
  },

  changeLanguage(locale: string): void {
    window.OneTrust?.changeLanguage(locale);
  },

  /* modified from https://forum.unity.com/styles/UnitySkin/onetrust-unity.js */
  blockNode(node: HTMLScriptElement, category: RuleType): void {
    const config = rules[category];
    if (!config) {
      return;
    }

    const hasDebugCookie = document.cookie.includes('onetrust-debug=');
    for (const rule of Object.values(config)) {
      const nodeSrc = node.src;
      if (nodeSrc) {
        const hasMatchingSrc = node.src.match(rule.src);
        const shouldRemove = rule.src && hasMatchingSrc && rule.tag && node.tagName.match(rule.tag);
        if (shouldRemove) {
          if (hasDebugCookie) {
            console.info('Blocking node', node, 'from block rule ', rule);
          }
          // Remove the source attribute for all nodes. Its value already cached in `nodeSrc`.
          node.removeAttribute('src');
          // Block script by removing its source, and changing its type.
          if (node.tagName === 'SCRIPT') {
            node.setAttribute('data-script-src', nodeSrc);
            node.setAttribute('type', 'text/plain');
          }
        }
      }

      if (rule.objects !== undefined) {
        for (const object of rule.objects) {
          window[object] = undefined;
        }
      }
    }
  },

  filterNode(nodes: Iterable<Node | Element>): void {
    for (const node of Array.from(nodes)) {
      // The added node must be an element.
      // And must be an element of type SCRIPT for now
      // (may add IFRAME and OBJECT later)
      if (node.nodeType === Node.ELEMENT_NODE && (node as Element).tagName === 'SCRIPT') {
        for (const [categoryName, id] of Object.entries(cookieCategories)) {
          const allowed = window.OnetrustActiveGroups?.match(id);
          if (!allowed) {
            this.blockNode(node as HTMLScriptElement, categoryName as RuleType);
          }
        }
      }
    }
  },

  filterScripts(): void {
    const nodes = document.getElementsByTagName('SCRIPT');
    this.filterNode(nodes);
  },

  observerCallback(mutations: MutationRecord[]): void {
    for (const mutation of mutations) {
      this.filterNode(mutation.addedNodes);
    }
  },

  setBlocker(): void {
    if (!window.OnetrustActiveGroups) {
      return;
    }

    // Scripts inserted during page load
    this.filterScripts();

    // Scripts loaded dynamically
    this.observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        this.filterNode(mutation.addedNodes);
      }
    });
    this.observer.observe(document.documentElement, {
      attributes: true,
      childList: true,
      subtree: true,
    });
  },

  hasConsented(): boolean {
    return Boolean(jsCookie.get(CONSENT_BANNER_COOKIE));
  },

  addConsentChangedListener(callback: Listener): void {
    window.consentChangedListeners.add(callback);
  },

  removeConsentChangedListener(callback: Listener): void {
    window.consentChangedListeners.delete(callback);
  },

  clearObserver(): void {
    this.observer?.disconnect();
    this.observer = null;
  },
};

export default onetrust;
